/**
 * @module Experience/pending-order.model
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
// Interfaces
import {
  ProductDetails,
  PendingOrdersDataDetails,
  LineDetail,
} from '@experience/change-plan-services/interface/pending-orders-data.interface';
/* import {
    PendingOrderInformationInterface, Line, ItemDetails
} from '@support/api/proxies/responses/api-2814-getOrderDetails.interface';*/
import {
  OrderSummaryInterface,
  LineDetails,
  Product,
} from '@support/api/proxies/responses/api-2814-new.interface';

// Constants
import { CPS_CONSTANTS } from '@experience/change-plan-services/constants/change-plan-services.constants';

export class PendingOrderModel {
  /**
   * Interface to Hold Order Details
   * @type {PendingOrdersDataDetails}
   * @memberof PendingOrderModel
   */
  public pendingOrdersDataDetails: PendingOrdersDataDetails;
  public lineData: LineDetails;

  constructor(pendingOrderData: OrderSummaryInterface) {
    this.intializePendingOrderModel();
    this.setPendingOrdersDetails(pendingOrderData);
  }

  /**
   * Filters the pending orders data based on whether product type is service or plan
   * @return void
   */
  private setPendingOrdersDetails(pendingOrderData: OrderSummaryInterface): void {
    if (pendingOrderData) {
      this.pendingOrdersDataDetails.id = pendingOrderData.orderId;
      if (pendingOrderData.orderDate) {
        this.pendingOrdersDataDetails.orderDate = pendingOrderData.orderDate.display;
      }
      this.pendingOrdersDataDetails.orderStatus = pendingOrderData.status;
      this.pendingOrdersDataDetails.effectiveDate = pendingOrderData.startTime;
      this.pendingOrdersDataDetails.isOrderCancelled =
        pendingOrderData.status === CPS_CONSTANTS.CANCELED ? true : false;
      if (pendingOrderData.orderItems !== null) {
        this.setFilteredPendingItem(pendingOrderData.orderItems.lines);
      }
    }
  }
  /**
   * Filters the plans and services for each line.
   * @param Items as an array
   */
  private setFilteredPendingItem(orderLine: LineDetails[]): void {
    orderLine.forEach((line: LineDetails) => {
      const lineData: LineDetail = this.intializeLineDetails();
      lineData.msisdn = line.msisdn;
      lineData.nickName = line.lineName;
      line.plans.forEach((filteredPlan: Product) => {
        this.setFilteredItem(filteredPlan, 'plans', lineData);
      });
      line.services.forEach((filteredService: Product) => {
        this.setFilteredItem(filteredService, 'services', lineData);
      });
      this.pendingOrdersDataDetails.lineDetails.push(lineData);
    });
  }

  /**
   * Filters the pending orders data based on whether product type is service or plan
   * @param filterProduct
   * @param type
   */
  private setFilteredItem(filterProduct: Product, type: string, lineData: LineDetail): void {
    let product: ProductDetails;
    product = {};
    product.offerCode = filterProduct.offerId;
    if (filterProduct.amount) {
      product.productAmount = filterProduct.amount.display;
    }
    product.productDescription = filterProduct.description;
    product.productName = filterProduct.offerName;
    if (type === CPS_CONSTANTS.SERVICES) {
      this.updateOrderDetails(
        filterProduct,
        CPS_CONSTANTS.SERVICES.toLowerCase(),
        product,
        lineData
      );
    } else {
      this.updateOrderDetails(filterProduct, CPS_CONSTANTS.PLANS.toLowerCase(), product, lineData);
    }
  }

  /**
   * update the pending orders model based on whether product type is service or plan
   */
  private updateOrderDetails(
    filterProduct: Product,
    key: string,
    product: ProductDetails,
    lineData: LineDetail
  ): void {
    if (filterProduct.actionCode && filterProduct.actionCode.toUpperCase() === CPS_CONSTANTS.ADD) {
      lineData[key].added.push(product);
    } else if (
      filterProduct.actionCode &&
      filterProduct.actionCode.toUpperCase() === CPS_CONSTANTS.REMOVE
    ) {
      lineData[key].removed.push(product);
    }
  }

  /**
   * Intializes the Pending Order Model
   * @memberof PendingOrderModel
   */
  public intializePendingOrderModel(): void {
    this.pendingOrdersDataDetails = {
      id: '',
      effectiveDate: '',
      orderDate: null,
      effectiveTime: null,
      isOrderCancelled: false,
      orderType: '',
      orderStatus: '',
      lineDetails: [],
      productSubType: [],
    };
  }
  /**
   * Intializes the line details variable
   * @memberof PendingOrderModel
   */
  public intializeLineDetails(): LineDetail {
    let lineData: LineDetail;
    lineData = {
      msisdn: '',
      nickName: '',
      plans: {
        added: [],
        removed: [],
      },
      services: {
        added: [],
        removed: [],
      },
    };
    return lineData;
  }

  /**
   * gets the pending order model data
   * @returns {PendingOrdersData}
   * @memberof PendingOrderModel
   */
  public getPendingOrdersInfo(): PendingOrdersDataDetails {
    return this.pendingOrdersDataDetails;
  }
}
